// NudgesAdminPage.js — Admin Nudges (2026-06-14)
//
// Admin-only VISIBILITY queue at /admin/nudges. Lists `nudge_candidates` docs
// that the Mac app (NudgeCandidateStore) writes when a "needs your judgment"
// item lands — v1: LEARN wisdom-rule proposals (success + fail-rate). Lets the
// admin read each one (origin, the proposed rule, evidence, target) and triage
// it (Mark reviewed / Dismiss) by flipping `reviewState` directly. This is the
// visibility MVP — actual approval still happens in-app / via the draft-PR flow.
//
// Auth: same UID allowlist + custom-claim pattern as the other admin queues.

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import {
  getFirestore, collection, query, orderBy, limit, getDocs,
  doc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

const TYPE_FILTERS = [
  { id: 'all', label: 'All types' },
  { id: 'learn-proposal', label: 'LEARN proposals' },
  { id: 'escalation-canary', label: 'FrugalGPT canaries' }
];

const STATE_FILTERS = [
  { id: 'pending', label: 'Pending' },
  { id: 'reviewed', label: 'Reviewed' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'all', label: 'All' }
];

function isAdmin(user) {
  if (!user) return false;
  if (ADMIN_UIDS.includes(user.uid)) return true;
  return user.idTokenResult?.claims?.role === 'admin';
}

function originBadge(origin) {
  const map = {
    success: { bg: '#064e3b', fg: '#6ee7b7', label: 'SUCCESS' },
    'fail-rate': { bg: '#451a1a', fg: '#fca5a5', label: 'FAIL-RATE' }
  };
  const s = map[origin] || { bg: '#1e293b', fg: '#94a3b8', label: (origin || 'rule').toUpperCase() };
  return (
    <span style={{ background: s.bg, color: s.fg, borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
      {s.label}
    </span>
  );
}

export default function NudgesAdminPage() {
  const auth = getAuth();
  const [user, setUser] = useState(auth.currentUser);
  const [authChecked, setAuthChecked] = useState(false);
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('pending');
  const [busy, setBusy] = useState({}); // id → action in flight

  useEffect(() => {
    return auth.onAuthStateChanged(async (u) => {
      if (u) {
        try {
          const tokenResult = await u.getIdTokenResult();
          setUser({ ...u, idTokenResult: tokenResult });
        } catch {
          setUser(u);
        }
      } else {
        setUser(null);
      }
      setAuthChecked(true);
    });
  }, [auth]);

  const loadNudges = useCallback(async () => {
    if (!user || !isAdmin(user)) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const db = getFirestore();
      const q = query(collection(db, 'nudge_candidates'), orderBy('createdAt', 'desc'), limit(200));
      const snap = await getDocs(q);
      setNudges(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setErrorMsg(`Load failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadNudges(); }, [loadNudges]);

  const filtered = nudges.filter(n => {
    if (typeFilter !== 'all' && (n.nudgeType || 'learn-proposal') !== typeFilter) return false;
    const state = n.reviewState || 'pending';
    if (stateFilter !== 'all' && state !== stateFilter) return false;
    return true;
  });

  const setReviewState = async (n, reviewState) => {
    setBusy(prev => ({ ...prev, [n.id]: reviewState }));
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'nudge_candidates', n.id), {
        reviewState,
        reviewedAt: serverTimestamp(),
        reviewedBy: user.uid
      });
      await loadNudges();
    } catch (err) {
      setErrorMsg(`Update failed: ${err.message}`);
    } finally {
      setBusy(prev => { const c = { ...prev }; delete c[n.id]; return c; });
    }
  };

  const wrap = { minHeight: '100vh', background: '#0f172a', color: '#f8fafc', padding: '24px 28px', fontFamily: 'system-ui, sans-serif' };

  if (!authChecked) return <div style={wrap}>Loading…</div>;
  if (!user) return <div style={wrap}>Sign in required.</div>;
  if (!isAdmin(user)) return <div style={wrap}>Access denied — this page is restricted to mChatAI admins.</div>;

  const pendingCount = nudges.filter(n => (n.reviewState || 'pending') === 'pending').length;

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 8 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Nudges <span style={{ color: '#64748b', fontWeight: 400, fontSize: 15 }}>· pending evaluations</span></h1>
        <Link to="/admin" style={{ color: '#60a5fa', fontSize: 14 }}>← Admin</Link>
        <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 13 }}>{pendingCount} pending · {nudges.length} total</span>
      </div>
      <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 0, maxWidth: 720 }}>
        Items your Mac flagged for judgment (LEARN "DO"/"AVOID" wisdom-rule proposals; FrugalGPT canaries later).
        Read-only here — approve in-app or via the draft PR; use Mark&nbsp;reviewed / Dismiss to triage.
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '12px 0 18px' }}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={selStyle}>
          {TYPE_FILTERS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} style={selStyle}>
          {STATE_FILTERS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <button onClick={loadNudges} disabled={loading} style={btnStyle}>{loading ? 'Loading…' : 'Reload'}</button>
      </div>

      {errorMsg && <div style={{ background: '#451a1a', color: '#fca5a5', padding: 10, borderRadius: 6, marginBottom: 14 }}>{errorMsg}</div>}

      {filtered.length === 0 && !loading && (
        <div style={{ color: '#64748b', padding: 24, textAlign: 'center' }}>
          No nudges match this filter. (They appear here when your Mac queues a LEARN proposal while self-healing is on.)
        </div>
      )}

      {filtered.map(n => {
        const state = n.reviewState || 'pending';
        return (
          <div key={n.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 16, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              {n.nudgeType === 'learn-proposal' && originBadge(n.ruleOrigin)}
              <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#e2e8f0' }}>{n.triggerSignature || n.title}</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: state === 'pending' ? '#fbbf24' : '#64748b' }}>{state}</span>
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 6 }}>
              {n.targetPath && <span>target <code style={{ color: '#cbd5e1' }}>{n.targetPath}</code> · </span>
              }
              {n.proposerProvider && <span>by {n.proposerProvider} · </span>}
              {typeof n.triggerCount === 'number' && <span>n={n.triggerCount} · </span>}
              <span>{n.proposalCreatedAt ? new Date(n.proposalCreatedAt).toLocaleString() : ''}</span>
            </div>
            {n.rationale && <div style={{ fontSize: 13, color: '#cbd5e1', marginBottom: 8 }}>{n.rationale}</div>}
            {n.proposedContent && (
              <details>
                <summary style={{ cursor: 'pointer', color: '#60a5fa', fontSize: 13 }}>Proposed content</summary>
                <pre style={{ background: '#0b1220', color: '#e2e8f0', padding: 12, borderRadius: 6, overflow: 'auto', maxHeight: 320, fontSize: 12, marginTop: 8 }}>{n.proposedContent}</pre>
              </details>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={() => setReviewState(n, 'reviewed')} disabled={!!busy[n.id] || state === 'reviewed'} style={{ ...btnStyle, background: '#065f46' }}>
                {busy[n.id] === 'reviewed' ? '…' : 'Mark reviewed'}
              </button>
              <button onClick={() => setReviewState(n, 'dismissed')} disabled={!!busy[n.id] || state === 'dismissed'} style={{ ...btnStyle, background: '#7f1d1d' }}>
                {busy[n.id] === 'dismissed' ? '…' : 'Dismiss'}
              </button>
              {state !== 'pending' && (
                <button onClick={() => setReviewState(n, 'pending')} disabled={!!busy[n.id]} style={{ ...btnStyle, background: '#334155' }}>
                  Reopen
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const selStyle = { background: '#1e293b', color: '#f8fafc', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 14 };
const btnStyle = { background: '#1e40af', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' };
